import React from "react";
import { motion } from "framer-motion";
import HomeLayout from "../../layouts/home.layout";
import { PropsWithClassName } from "@/types/global";
import { Center, Col, Row, VerticalCenter } from "../css";

const Loader: React.FC = () => {
  return (
    <HomeLayout>
      <div className="flex flex-col items-center justify-center h-screen md:h-full ">
        <motion.div
          className="w-20 h-20 rounded-full"
          style={{
            background: "conic-gradient(from 0deg, #FDF7FF, #FDF7FF, #FDF7FF, #C59EF7, #9A6AD4, #6F36BC)",
            maskImage: "radial-gradient(transparent 55%, black 55%)",
            WebkitMaskImage: "radial-gradient(transparent 55%, black 55%)",
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        <motion.h2
          className="mt-4 text-13 font-normal !leading-normal text-gray-700"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Getting things ready...
        </motion.h2>
      </div>
    </HomeLayout>
  );
};

export default Loader;

export const RoundLoader: React.FC<PropsWithClassName> = ({ className = "" }) => {
  return (
    <div className={`flex flex-col items-center justify-center h-full ${className}`}>
      <motion.div
        className="w-12 h-12 rounded-full"
        style={{
          background: "conic-gradient(from 0deg, #FDF7FF, #FDF7FF, #FDF7FF, #C59EF7, #9A6AD4, #6F36BC)",
          maskImage: "radial-gradient(transparent 50%, black 50%)",
          WebkitMaskImage: "radial-gradient(transparent 50%, black 50%)",
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
};

const skeletonBlock: any = {
  hidden: { opacity: 0 },
  visible: (i: number) => ({
    opacity: 1,
    transition: {
      delay: i * 0.05,
      duration: 0.4,
      ease: "easeInOut",
      repeat: Infinity,
      repeatType: "reverse",
    },
  }),
};

export const FormSkeleton = () => {
  const fields = new Array(14).fill(0);

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 gap-4">
        {fields.map((_, i) => (
          <motion.div
            key={i}
            className="h-10 bg-gray-200 rounded-md w-full"
            custom={i}
            variants={skeletonBlock}
            initial="hidden"
            animate="visible"
          ></motion.div>
        ))}
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <motion.div
          className="h-10 w-24 bg-gray-300 rounded-md"
          custom={fields.length + 1}
          variants={skeletonBlock}
          initial="hidden"
          animate="visible"
        ></motion.div>
        <motion.div
          className="h-10 w-24 bg-gray-300 rounded-md"
          custom={fields.length + 2}
          variants={skeletonBlock}
          initial="hidden"
          animate="visible"
        ></motion.div>
      </div>
    </div>
  );
};

const employeeSkeletonBlock: any = {
  hidden: { opacity: 0.3 },
  visible: (i: number) => ({
    opacity: [0.3, 0.8, 0.3],
    transition: {
      delay: i * 0.1,
      duration: 1.5,
      ease: "easeInOut",
      repeat: Infinity,
      repeatType: "loop",
    },
  }),
};

export const OverviewSectionSkeleton = () => {
  return (
    <div className="flex w-full flex-col h-full p-6">
      {/* Content Grid Skeleton */}
      <motion.div className="h-9 w-32 bg-gray-200 mb-5 " custom={4} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
      <div className="grid grid-cols-2 gap-x-16 gap-y-6">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="space-y-2">
            <motion.div className="h-4 w-24 bg-gray-200 rounded" custom={11 + i * 2} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
            <motion.div className="h-5 w-full bg-gray-200 rounded" custom={12 + i * 2} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
          </div>
        ))}
      </div>
    </div>
  );
};
export const EmployeeDetailsSubSection = () => {
  return (
    <Col className="gap-6 p-4">
      <motion.div className="h-9 w-32 bg-gray-200" custom={4} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
      <Col>
        <motion.div className="h-9 w-full bg-gray-200 mb-5 " custom={4} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
        <div className="grid grid-cols-2 gap-x-16 gap-y-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="space-y-2">
              <motion.div className="h-4 w-24 bg-gray-200 rounded" custom={11 + i * 2} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
              <motion.div className="h-5 w-full bg-gray-200 rounded" custom={12 + i * 2} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
            </div>
          ))}
        </div>
      </Col>
      <Col>
        <motion.div className="h-9 w-full bg-gray-200 mb-5 " custom={4} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
        <div className="grid grid-cols-2 gap-x-16 gap-y-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
              <motion.div className="h-4 w-24 bg-gray-200 rounded" custom={11 + i * 2} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
              <motion.div className="h-5 w-full bg-gray-200 rounded" custom={12 + i * 2} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
            </div>
          ))}
        </div>
      </Col>
    </Col>
  );
};

// Activities Skeleton Component
export const ActivitiesSkeleton = () => {
  return (
    <div className="flex w-full flex-col h-full px-4">
      {/* Activity items */}
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-start gap-3 p-3 border-b">
            <motion.div className="w-4 h-4 bg-gray-200 rounded mt-1" custom={8 + i * 4} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
            <div className="flex-1 space-y-2">
              <motion.div className="h-4 w-3/4 bg-gray-200 rounded" custom={9 + i * 4} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <motion.div
                    className="w-6 h-6 rounded-full bg-gray-200"
                    custom={10 + i * 4}
                    variants={employeeSkeletonBlock}
                    initial="hidden"
                    animate="visible"
                  />
                  <motion.div
                    className="h-3 w-20 bg-gray-200 rounded"
                    custom={11 + i * 4}
                    variants={employeeSkeletonBlock}
                    initial="hidden"
                    animate="visible"
                  />
                </div>
                <motion.div className="h-3 w-16 bg-gray-200 rounded" custom={12 + i * 4} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Attachments Skeleton Component
export const AttachmentsSkeleton = () => {
  return (
    <div className="flex w-full flex-col h-full px-4">
      {/* Table header */}
      <div className="grid grid-cols-5 gap-4 p-3 border-b bg-gray-50 mb-4">
        <motion.div className="h-4 w-20 bg-gray-200 rounded" custom={6} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
        <motion.div className="h-4 w-16 bg-gray-200 rounded" custom={7} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
        <motion.div className="h-4 w-16 bg-gray-200 rounded" custom={8} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
        <motion.div className="h-4 w-20 bg-gray-200 rounded" custom={9} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
        <motion.div className="h-4 w-20 bg-gray-200 rounded" custom={10} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
      </div>

      {/* Table rows */}
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="grid grid-cols-5 gap-4 p-3 border-b items-center">
            <div className="flex items-center gap-2">
              <motion.div className="w-6 h-6 bg-gray-200 rounded" custom={11 + i * 6} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
              <motion.div className="h-4 w-24 bg-gray-200 rounded" custom={12 + i * 6} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
            </div>
            <motion.div className="h-4 w-20 bg-gray-200 rounded" custom={13 + i * 6} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
            <motion.div className="h-4 w-16 bg-gray-200 rounded" custom={14 + i * 6} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
            <motion.div className="h-4 w-28 bg-gray-200 rounded" custom={15 + i * 6} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
            <motion.div className="h-4 w-24 bg-gray-200 rounded" custom={16 + i * 6} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t">
        <motion.div className="h-4 w-32 bg-gray-200 rounded" custom={35} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
        <div className="flex items-center gap-2">
          <motion.div className="h-8 w-8 bg-gray-200 rounded" custom={36} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
          <motion.div className="h-8 w-8 bg-gray-200 rounded" custom={37} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
          <motion.div className="h-8 w-8 bg-gray-200 rounded" custom={38} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
        </div>
      </div>
    </div>
  );
};

const SkeletonComponentMap: any = {
  overview: OverviewSectionSkeleton,
  "employee-details": EmployeeDetailsSubSection,
  "exit-details": OverviewSectionSkeleton,
  attachments: AttachmentsSkeleton,
  activities: ActivitiesSkeleton,
};

// Employee Details Skeleton Component
export const EmployeeDetailsSkeleton = ({ tab }: { tab?: string }) => {
  const hideNotes = tab !== "overview";
  const SkeletonComponent = tab ? SkeletonComponentMap[tab] : OverviewSectionSkeleton;
  return (
    <div className="flex flex-col flex-1 h-full">
      {/* Header Skeleton */}
      <div className="px-4 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between flex-wrap">
          <Center className="">
            {/* Profile Picture Skeleton */}
            <motion.div
              className="size-12 2xl:size-14 rounded-full bg-gray-200"
              custom={0}
              variants={employeeSkeletonBlock}
              initial="hidden"
              animate="visible"
            />
            <VerticalCenter className="px-2 !items-start">
              <div className="flex items-center gap-4 w-full">
                {/* Name Skeleton */}
                <motion.div className="h-6 w-44 bg-gray-200 rounded" custom={1} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
                {/* Status Badge Skeleton */}
                <motion.div className="h-6 w-20 bg-gray-200 rounded-md" custom={2} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
              </div>
              {/* Address Skeleton */}
              <motion.div className="h-4 w-56 bg-gray-200 rounded mt-1" custom={3} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
            </VerticalCenter>
          </Center>
          <Center className="gap-2 h-full hidden md:flex ">
            {/* Buttons Skeleton */}
            <motion.div className="h-9 w-32 bg-gray-200 rounded-50" custom={4} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
            <motion.div className="h-9 w-28 bg-gray-200 rounded" custom={5} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />
          </Center>
        </div>
      </div>
      <Row className="w-full h-[calc(100%-100px)] relative">
        {/* Sidebar Skeleton */}
        <div className="hidden md:block h-full bg-containerBg w-[216px] shadow-sm rounded-llg">
          <div className="p-3">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="h-8 bg-gray-200 rounded-md mb-2"
                custom={6 + i}
                variants={employeeSkeletonBlock}
                initial="hidden"
                animate="visible"
              />
            ))}
          </div>
        </div>

        {/* Main Content Skeleton */}
        <div className="w-full md:w-[calc(100%-216px)] flex h-full border md:mx-3 rounded-llg">
          <div className={` ${hideNotes ? "w-full" : "w-full md:w-[70%]"} h-full flex pt-2`}>
            <SkeletonComponent />
          </div>
          {/* Quick Notes Sidebar Skeleton */}
          {!hideNotes && (
            <div className="hidden md:block w-[30%] h-full pt-[50px] overflow-y-auto p-4">
              {/* Add Note Button Skeleton */}
              <motion.div className="h-12 w-full bg-gray-200 rounded-md mb-4" custom={27} variants={employeeSkeletonBlock} initial="hidden" animate="visible" />

              {/* Notes List Skeleton */}
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-3 border-b pb-3">
                    <motion.div
                      className="h-4 w-full bg-gray-200 rounded"
                      custom={28 + i * 3}
                      variants={employeeSkeletonBlock}
                      initial="hidden"
                      animate="visible"
                    />
                    <motion.div
                      className="h-4 w-3/4 bg-gray-200 rounded"
                      custom={29 + i * 3}
                      variants={employeeSkeletonBlock}
                      initial="hidden"
                      animate="visible"
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <motion.div
                          className="w-6 h-6 rounded-full bg-gray-200"
                          custom={30 + i * 3}
                          variants={employeeSkeletonBlock}
                          initial="hidden"
                          animate="visible"
                        />
                        <motion.div
                          className="h-3 w-16 bg-gray-200 rounded"
                          custom={31 + i * 3}
                          variants={employeeSkeletonBlock}
                          initial="hidden"
                          animate="visible"
                        />
                      </div>
                      <motion.div
                        className="h-3 w-12 bg-gray-200 rounded"
                        custom={32 + i * 3}
                        variants={employeeSkeletonBlock}
                        initial="hidden"
                        animate="visible"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Row>
    </div>
  );
};
